import React from 'react';
import styled from '@emotion/styled';
import PropTypes, { InferProps } from 'prop-types';
import { ImageContainer } from '../ImageGrid';
import Anchor from '../Anchor';
import InnerHtmlStyling from '../InnerHtmlStyling';
import Headline4 from '../Typography/Heading/Headline4';
import breakpoints from '../Theme/breakpoints';

const StyledHeadline4 = styled(Headline4)`
  justify-self: center;
  align-self: center;
  padding: 24px 0;
`;

const DefaultWrapper = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  display: block;
  text-align: center;
  padding: 48px 16px;

  @media (min-width: ${breakpoints.sm}px) {
    padding: 24px 16px;
  }
`;

const Wrapper = styled(DefaultWrapper)`
  @media (min-width: ${breakpoints.sm}px) {
    display: flex;
    padding: 16px;
    justify-content: space-around;
  }
`;

const Background = styled.div<{ backgroundColor: string }>`
  background-color: ${(properties) => properties.backgroundColor};
`;

const Logo = styled.div`
  max-width: 102px;
  margin: 0 auto;

  @media (min-width: ${breakpoints.sm}px) {
    margin: 0;
    align-self: center;
  }
`;

export const CompanyProfileTabsWrapper = styled.div`
  max-width: 575px;
  margin: 0 auto;
  padding: 12px 0 24px;

  @media (min-width: ${breakpoints.md}px) {
    padding: 24px 0 24px;
  }

  @media (min-width: ${breakpoints.lg}px) {
    padding: 56px 0 24px;
  }

  .MuiTab-root {
    font-size: 0.85rem;
    letter-spacing: 0;
  }
`;

export const BreadcrumbsWrapper = styled.div<{ isProfilePage?: boolean }>`
  margin: 0;
  padding: ${({ isProfilePage }) => (isProfilePage ? '0 24px' : '0 16px')};
  max-width: 100%;

  @media (min-width: ${breakpoints.md}px) {
    margin: ${({ isProfilePage }) => (isProfilePage ? 'auto' : '0 24px')};
    max-width: ${({ isProfilePage }) => (isProfilePage ? '575px' : '100%')};
    padding: 0;
  }
`;

const brandHeaderPropertyTypes = {
  logo: PropTypes.node,
  brandName: PropTypes.string,
  backgroundColor: PropTypes.string,
};

type BrandHeaderProperties = InferProps<typeof brandHeaderPropertyTypes>;

export const BrandHeader = ({
  logo,
  brandName,
  backgroundColor,
}: BrandHeaderProperties) => (
  <Background backgroundColor={backgroundColor ?? 'rgb(239,239,239)'}>
    {logo ? (
      <Wrapper>
        {logo && (
          <Logo>
            {logo}
          </Logo>
        )}
        {brandName && (
          <StyledHeadline4 tag="h1">{brandName}</StyledHeadline4>
        )}
        <div />
      </Wrapper>
    )
      : (
        <DefaultWrapper>
          {brandName && (
            <StyledHeadline4 tag="h1">{brandName}</StyledHeadline4>
          )}
        </DefaultWrapper>
      )}
  </Background>
);

const Container = styled.main`
  max-width: 575px;
  margin: 32px auto 0;
  padding: 0 24px;
  position: relative;
`;

export const ProductsContainer = styled.div`
  margin-top: 24px;
`;

const SpacingContainer = styled.div`
  margin: 24px 0;
`;

const StyledInnerHtmlStyling = styled(InnerHtmlStyling)`
  white-space: pre-wrap;
  font-family: ${({ theme }) => theme.fonts.secondary};
`;

const FlexContainer = styled.dl`
  display: flex;
  flex-direction: column;
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.sizes.h5};
  color: ${({ theme }) => theme.colors.blackHigh};
  margin: .5rem 0;

  @media (min-width: ${breakpoints.sm}px) {
    flex-direction: row;
  }
`;

const StyledDescriptionTerm = styled.dt`
  padding-bottom: 8px;
  font-size: 16px;
  line-height: 1.75rem;

  @media (min-width: ${breakpoints.sm}px) {
    padding-bottom: 0;
    padding-right: 8px;
  }
`;

const StyledDescriptionName = styled.dd`
  display: flex;
  flex-wrap: wrap;
  overflow-wrap: break-word;
  font-size: 16px;
  font-weight: bold;
  margin: auto 0;
  white-space: pre-wrap;
  line-height: 1.75rem;

  span {
    padding-right: 8px;
  }
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
`;

const profileContentPropertyTypes = {
  industry: PropTypes.arrayOf(PropTypes.node),
  country: PropTypes.string,
  website: PropTypes.string,
  about: PropTypes.node,
  description: PropTypes.string,
  impressionImage: PropTypes.node,
  video: PropTypes.node,
  imageCollection: PropTypes.arrayOf(PropTypes.node),
  policy: PropTypes.node,
  editButton: PropTypes.node,
  socialShare: PropTypes.node,
  contactDetails: PropTypes.string,
};

type ProfileContentProperties = InferProps<typeof profileContentPropertyTypes>;

const ProfileContent = ({
  industry,
  country,
  website,
  about,
  description,
  impressionImage,
  video,
  imageCollection,
  policy,
  editButton,
  socialShare,
  contactDetails,
}: ProfileContentProperties) => (
  <Container>
    {editButton}
    <DetailsContainer>
      <div>
        {industry?.[0] && (
        <FlexContainer>
          <StyledDescriptionTerm>Products:</StyledDescriptionTerm>
          <StyledDescriptionName>
            {industry}
          </StyledDescriptionName>
        </FlexContainer>
        )}
        {country && (
        <FlexContainer>
          <StyledDescriptionTerm>Country:</StyledDescriptionTerm>
          <StyledDescriptionName>
            {country}
          </StyledDescriptionName>
        </FlexContainer>
        )}
      </div>
      <div>
        {socialShare}
      </div>
    </DetailsContainer>
    {contactDetails && (
      <FlexContainer>
        <StyledDescriptionTerm>Contact:</StyledDescriptionTerm>
        <StyledDescriptionName>
          {contactDetails}
        </StyledDescriptionName>
      </FlexContainer>
    )}
    {
      about
        ? (
          <SpacingContainer>
            <StyledInnerHtmlStyling>
              {about}
            </StyledInnerHtmlStyling>
          </SpacingContainer>
        )
        : description && (
          <SpacingContainer>
            <StyledInnerHtmlStyling>
              <p>
                {description}
              </p>
            </StyledInnerHtmlStyling>
          </SpacingContainer>
        )
    }
    {impressionImage && (
      <SpacingContainer>
        {impressionImage}
      </SpacingContainer>
    )}
    {video && (
      <SpacingContainer>
        {video}
      </SpacingContainer>
    )}
    {imageCollection?.[0] ? (
      <SpacingContainer>
        <ImageContainer>
          {imageCollection}
        </ImageContainer>
      </SpacingContainer>
    )
      : (
        <div>
          {imageCollection}
        </div>
      )}
    {policy && (
      <SpacingContainer>
        <StyledInnerHtmlStyling>
          {policy}
        </StyledInnerHtmlStyling>
      </SpacingContainer>
    )}
    {website && (
      <FlexContainer>
        <StyledDescriptionTerm>Website:</StyledDescriptionTerm>
        <StyledDescriptionName>
          <Anchor href={website} target="_blank" rel="noopener">
            {website.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '')}
          </Anchor>
        </StyledDescriptionName>
      </FlexContainer>
    )}
  </Container>
);

ProfileContent.propTypes = profileContentPropertyTypes;

export default ProfileContent;
